import { IonButton, IonCheckbox, IonInput, IonLoading } from '@ionic/react';
import { Redirect, useHistory } from 'react-router-dom';
import { FormEvent, useMemo, useState } from 'react';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { auth } from '../../firebase';
import { useAuth } from '../../auth';
import { clearAuthAttempts, getAuthRetryAfter, recordAuthAttempt } from '../../utils/AuthRateLimit';
import '../home/AuthForms.css';

const REGISTER_WINDOW = 15 * 60_000;

const RegisterModal: React.FC = () => {
  const { loggedIn } = useAuth();
  const history = useHistory();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [website, setWebsite] = useState('');
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const passwordValid = useMemo(() => password.length >= 10 && /[a-z]/.test(password) && /[A-Z]/.test(password) && /\d/.test(password), [password]);

  const handleRegister = async (event: FormEvent) => {
    event.preventDefault();
    if (website) return;
    const retryAfter = getAuthRetryAfter('register', 3, REGISTER_WINDOW);
    if (retryAfter) {
      setErrorMessage(`Too many registration attempts. Please try again in about ${Math.ceil(retryAfter / 60)} minutes.`);
      return;
    }
    if (!passwordValid) return setErrorMessage('Use at least 10 characters with uppercase, lowercase and a number.');
    if (password !== confirm) return setErrorMessage('Passwords do not match.');
    if (!acceptedPrivacy) return setErrorMessage('Please read and accept the Privacy Policy to create an account.');

    try {
      setLoading(true);
      setErrorMessage('');
      recordAuthAttempt('register', REGISTER_WINDOW);
      const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      await sendEmailVerification(credential.user);
      clearAuthAttempts('register');
    } catch {
      setErrorMessage('The account could not be created. Please check your details or try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loggedIn) return <Redirect to="/my/training" />;

  return (
    <form className="auth-form" onSubmit={handleRegister}>
      <div className="auth-form__intro"><span>Private by design</span><h2>Create your account</h2><p>Authentication and the shared machine catalogue use Firebase. Fitness logs remain local.</p></div>
      <input className="auth-honeypot" tabIndex={-1} autoComplete="off" aria-hidden="true" value={website} onChange={event => setWebsite(event.target.value)} />
      <IonInput className="auth-input" label="Email" labelPlacement="stacked" fill="outline" type="email" autocomplete="email" value={email} onIonInput={event => setEmail(event.detail.value || '')} required />
      <IonInput className="auth-input" label="Password" labelPlacement="stacked" fill="outline" type="password" autocomplete="new-password" value={password} onIonInput={event => setPassword(event.detail.value || '')} required />
      <p className={`auth-password-hint${password && !passwordValid ? ' is-invalid' : ''}`}>10+ characters · uppercase · lowercase · number</p>
      <IonInput className="auth-input" label="Confirm password" labelPlacement="stacked" fill="outline" type="password" autocomplete="new-password" value={confirm} onIonInput={event => setConfirm(event.detail.value || '')} required />
      <label className="auth-consent-row"><IonCheckbox checked={acceptedPrivacy} onIonChange={event => setAcceptedPrivacy(event.detail.checked)} /><span>I have read the <button type="button" onClick={() => history.push('/privacy')}>Privacy Policy</button>.</span></label>
      {errorMessage && <p className="auth-error" role="alert">{errorMessage}</p>}
      <IonButton type="submit" expand="block" shape="round" disabled={loading || !email.trim() || !password || !confirm}>Create account</IonButton>
      <p className="auth-security-note">A verification email will be sent after registration.</p>
      <IonLoading isOpen={loading} message="Creating your account…" />
    </form>
  );
};

export default RegisterModal;
